import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Pagination,
  PaginationItem,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import FilterBar from "components/FilterBar";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setHeaderDetails } from "store/actions/header";
import {
  changePagination,
  cleanAllNutritionData,
  deleteNutrition,
  getInitialNutrition,
  getNutritions,
  searchNutritions,
} from "store/actions/nutritions";
import useDocumentTitle from "utils/hooks/useDocumentTitle";
import { programStatuses } from "utils/programs.utils";
import ConfirmationModel from "../../components/ConfirmationModel";
import Loader from "../../components/Loader";
import NutritionItem from "./NutritionItem";
import useSubscriptionGuard from "hooks/useSubscriptionGuard";
import NutritionTitles from "./NutritionTitles";

const NutritionPlans = () => {
  useDocumentTitle("Mes Programmes Nutrition", true);
  const { canCreate } = useSubscriptionGuard();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nutritions = useSelector((state) => state.nutritions.nutritions);
  const currentPage = useSelector((state) => state.nutritions.currentPage);
  const isLoadingNutritins = useSelector(
    (state) => state.nutritions.isLoadingNutritins
  );

  const [showDeleteModel, setDeleteModel] = useState(false);
  const [current, setCurrent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState([]);

  const { type } = useSelector((state) => state.user.userDetails);
  const isAdmin = type?.toUpperCase() === "ADMIN";
  const [tabValue, setTabValue] = useState(isAdmin ? 2 : 0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    dispatch(getNutritions(newValue));
  };

  useEffect(() => {
    dispatch(getNutritions(tabValue));
    const navDetail = {
      title: "Programmes de nutrition",
      isBreadcrumns: false,
    };

    dispatch(setHeaderDetails(navDetail));
  }, []);

  const toggleDeleteModel = (response = null) => {
    setCurrent(response);
    setDeleteModel(!showDeleteModel);
  };

  const start = (currentPage - 1) * 10;
  const end = start + 10;
  const currentPageItems = (nutritions || []).slice(start, end).length;

  if (isLoadingNutritins) {
    return <Loader isLoading={isLoadingNutritins} />;
  }

  const getStatusFilterValues = (values) => {
    setFilters(values);
    dispatch(searchNutritions(searchQuery, values));
  };
  return (
    <>
      <Container
        maxWidth="xxl"
        className="header-box"
        sx={{ padding: "0 !important", marginBottom: "20px" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={1}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1}
          >
            <TextField
              placeholder="Rechercher un programme"
              name="searchNutrtionPlan"
              size="small"
              sx={{
                width: { sm: "100%", md: "414px" },
                ".MuiInputBase-root": {
                  background: "#ffffff",
                },
              }}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                dispatch(searchNutritions(e.target.value, filters));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FilterBar
              filters={[
                { title: "Statut", list: programStatuses, onSelect: getStatusFilterValues },
              ]}
            />
          </Stack>
          <Tooltip title={!canCreate ? "Mettez à jour votre abonnement pour accéder à cette fonctionnalité" : ""}>
            <span>
              <Button
                size="small"
                color="primary"
                variant="contained"
                disabled={!canCreate}
                sx={{ width: { xs: "100%", sm: "auto" } }}
                onClick={() => {
                  dispatch(cleanAllNutritionData());
                  dispatch(getInitialNutrition());
                  navigate("/nutrition/programs/create");
                }}
              >
                Créer un programme de nutrition
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Container>
      <Container maxWidth="xxl">
        <Stack gap={3}>
          {!isAdmin && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                flexWrap: "nowrap",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                WebkitOverflowScrolling: "touch",
              }}
            >
              {[
                { label: "Mes Programmes", value: 0 },
                { label: "Mes Templates", value: 1 },
                { label: "Templates Evocoach", value: 2 },
              ].map((tab) => (
                <Box
                  key={tab.value}
                  onClick={() => handleTabChange(null, tab.value)}
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 0.75,
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: tabValue === tab.value ? 600 : 400,
                    color: tabValue === tab.value ? "#ffffff" : "#4F5668",
                    backgroundColor: tabValue === tab.value ? "#0001FC" : "#F0F2F5",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    transition: "all 0.15s ease",
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>
          )}
          <Stack gap={1} sx={{ minHeight: "647px" }}>
            <NutritionTitles />
            {(nutritions || []).length === 0 && !isLoadingNutritins && (
              <Box textAlign="center" py={6}>
                <Typography fontSize="20px" fontWeight="600" color="#4F5668">
                  {isAdmin
                    ? "Aucun template de nutrition créé pour l'instant."
                    : tabValue === 1
                      ? "Vous n'avez pas encore de templates personnels. Créez-en un dès maintenant !"
                      : tabValue === 2
                        ? "Les templates Evocoach arrivent très bientôt — restez connecté !"
                        : "Aucun programme de nutrition pour le moment. Lancez-vous et créez votre premier programme !"}
                </Typography>
              </Box>
            )}
            {(nutritions || []).slice(start, end).map((nutrition, index) => (
              <NutritionItem
                toggleDeleteModel={toggleDeleteModel}
                nutrition={nutrition}
                key={index}
                isTemplate={isAdmin || tabValue >= 1}
                isEvocoachTemplate={isAdmin || tabValue === 2}
              />
            ))}
          </Stack>

          {(nutritions || []).length > 10 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography color="#000000" fontSize="12px" fontWeight="500">
                Affichage de {start + 1} à {start + currentPageItems} sur{" "}
                {nutritions.length} résultats
              </Typography>
              <Pagination
                page={currentPage}
                onChange={(page, value) => dispatch(changePagination(value))}
                count={Math.ceil(nutritions.length / 10)}
                variant="outlined"
                shape="rounded"
                color="secondary"
                size="small"
                siblingCount={0}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                    {...item}
                  />
                )}
              />
            </Stack>
          )}
        </Stack>
        <ConfirmationModel
          isOpen={showDeleteModel}
          title="Supprimer le programme"
          body={[
            "Vous êtes sur le point de supprimer",
            <strong key="name"> {current?.name}</strong>,
            ". Cela le supprimera définitivement de votre bibliothèque.",
          ]}
          maxWidth="sm"
          handleClose={() => toggleDeleteModel()}
          onConfirm={() => {
            dispatch(deleteNutrition(current._id)).then(() =>
              toggleDeleteModel()
            );
          }}
        />
      </Container>
    </>
  );
};

export default NutritionPlans;
